import React, { useId, useState } from 'react';
import { Box, Button, Collapse, Typography, useMediaQuery } from '@mui/material';
import { planOptions, PLAN_OPTIONS_HEADER, PLAN_OPTION_TARGET } from '../utils/subscriptionCopy';

/**
 * The horizontal card at the top of Manage Plan, explaining the two ways to buy.
 *
 * The screen used to open straight onto three priced subscription cards, which
 * answers "which plan?" for someone who has already decided to subscribe and
 * answers nothing for anyone still deciding whether to. Credit packs — the
 * whole other half of the choice — were a text link below the fold.
 *
 * ── ONE CARD, TWO COLUMNS ───────────────────────────────────────────────────
 * The two options are alternatives, not steps, and they belong in the SAME
 * card: two cards side by side would read as two products to compare on price,
 * which is not the question being put. Inside it they are equal columns — each
 * with its own heading, its own body and its own link — because stacking them,
 * or heading the card as a whole, makes the first read as the recommendation
 * and the second as a footnote on it. That is the bias the card exists to
 * remove.
 *
 * Below the `md` breakpoint there is no honest way to show two columns of
 * prose, so they stack. That is a rendering constraint, not a change of
 * meaning: same order, same weight, neither promoted.
 *
 * ── COLLAPSED BY DEFAULT ────────────────────────────────────────────────────
 * REQUESTED: the card was tall enough to push the plans themselves below the
 * fold. Each column now opens on its heading and intro — the one or two lines
 * that say what the option IS — with the terms and the scroll link behind
 * "Show more". Nothing was removed and no copy changed: planOptions() already
 * split every column into `intro` and the rest, and that split is exactly the
 * visible/collapsed line.
 *
 * Collapsed content stays MOUNTED. MUI's Collapse marks it visibility:hidden
 * when shut, so its links drop out of the tab order and the accessibility tree
 * until opened, while the copy remains in the document.
 *
 * ── WHY THE LINKS SCROLL RATHER THAN NAVIGATE ───────────────────────────────
 * Both destinations are already on this page. Navigating away and back would
 * lose the billing-interval toggle and any dialog state, and a router push for
 * a target a few hundred pixels down is a page transition standing in for a
 * scroll. The caller owns the refs and passes one handler, so this component
 * does not need to know where anything is.
 */
const TEAL = '#1C7C73';
const TEAL_HOVER = '#156874';
const TEAL_LIGHT = '#E8F3F1';
const TEXT = '#1A1A1A';
const TEXT_MUTED = '#5A5F55';
const BORDER = '#E6E9E6';
const ORANGE_GRADIENT = 'linear-gradient(135deg, #BF3A1A 0%, #E86340 100%)';

/**
 * The two column buttons. REQUESTED (QA): "See subscription options" an orange
 * button with white text, "See pay-as-you-go option" a white button with green
 * text. These are the Credits page's own `primary` and `credits` treatments
 * (BUTTON_VARIANTS / BUTTON_BASE in CreditManagement.jsx), so the same kind of
 * button looks the same on both screens.
 */
const CTA_BASE = {
  borderRadius: '10px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: 14,
  px: 3.5,
  py: 1.25,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  '&:focus-visible': { outline: `2px solid ${TEAL}`, outlineOffset: '2px' },
};
export const CTA_VARIANTS = {
  // Orange fill, white text.
  primary: {
    background: ORANGE_GRADIENT,
    color: '#FFFFFF',
    boxShadow: '0 4px 14px rgba(190,58,26,0.30)',
    '&:hover': { background: ORANGE_GRADIENT, filter: 'brightness(1.06)' },
  },
  // White fill, green (teal) text and outline — outlined because a white
  // button on this white card would otherwise have no edge.
  secondary: {
    bgcolor: '#FFFFFF',
    color: TEAL,
    border: `1px solid ${TEAL}`,
    '&:hover': { bgcolor: TEAL_LIGHT },
  },
};

/**
 * The SUBSCRIPTION / PAY-AS-YOU-GO headers. REQUESTED (QA): "need to be more
 * prominent" — they were Inter 700 at 11px with wide tracking, a caption.
 * OLD: fontFamily 'Inter, sans-serif', fontWeight 700, fontSize 11,
 * letterSpacing '0.1em', mb 0.5. Same text and colour for both columns, so
 * neither side outranks the other.
 */
export const EYEBROW_SX = {
  fontFamily: 'Manrope, sans-serif',
  fontWeight: 800,
  fontSize: { xs: 16, md: 17 },
  lineHeight: 1.3,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: TEAL,
  mb: 0.75,
};

// Short on purpose — REQUESTED "smooth but subtle". MUI's default derives the
// duration from the content height, which runs to 300ms+ for the longer column.
const COLLAPSE_MS = 200;

/** A link that looks like a link and IS a button, because it acts on this page. */
const ScrollLink = ({ children, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      p: 0,
      border: 0,
      bgcolor: 'transparent',
      font: 'inherit',
      fontWeight: 700,
      color: TEAL,
      cursor: 'pointer',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
      '&:hover': { color: TEAL_HOVER },
    }}
  >
    {children}
  </Box>
);

/** One paragraph, which may carry a link mid-sentence. */
const Line = ({ segments, onGoTo, sx }) => (
  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 15, color: TEXT, ...sx }}>
    {segments.map((segment, i) => (
      segment.target
        ? (
          <ScrollLink key={i} onClick={() => onGoTo?.(segment.target)}>
            {segment.text}
          </ScrollLink>
        )
        : <React.Fragment key={i}>{segment.text}</React.Fragment>
    ))}
  </Typography>
);

/**
 * "Show more ▾" / "Show less ▴".
 *
 * The card's own teal and weight, so it reads as part of this card. No
 * underline, which is the one thing that tells it apart from the two scroll
 * links: those GO somewhere, this only opens what is already here.
 *
 * The arrow is aria-hidden so the name is just "Show more" / "Show less";
 * aria-expanded carries the state and aria-controls names the region.
 */
const MoreToggle = ({ open, onClick, controls }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-expanded={open}
    aria-controls={controls}
    sx={{
      p: 0,
      border: 0,
      bgcolor: 'transparent',
      fontFamily: 'Inter, sans-serif',
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1.5,
      color: TEAL,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      '&:hover': { color: TEAL_HOVER },
      '&:focus-visible': { outline: `2px solid ${TEAL}`, outlineOffset: '2px', borderRadius: '4px' },
    }}
  >
    {open ? 'Show less' : 'Show more'}
    <Box component="span" aria-hidden="true">{open ? '▴' : '▾'}</Box>
  </Box>
);

const PlanOptionsCard = ({ onGoTo }) => {
  const options = planOptions();
  const idBase = useId();
  // One flag per column, keyed by option.key: each opens and shuts on its own,
  // and a column absent from the map is collapsed — the default.
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  // Collapse does not honour reduced-motion by itself.
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  return (
    <Box
      component="section"
      aria-label="Your plan options"
      sx={{
        bgcolor: '#FFFFFF',
        border: `1px solid ${BORDER}`,
        borderRadius: '16px',
        // REQUESTED: more compact. Spacing only — every font size, weight,
        // colour and border is as it was. Was p { xs: 2.5, md: 3.5 }, mb 4.
        p: { xs: 2, md: 2.5 },
        mb: 3,
      }}
    >
      {/* REQUESTED: the card carries the header, and the two options sit under
          it. It names what the card IS — the ways to buy — rather than either
          of the options, which is what keeps a banner across the top from
          making the left column the subject and the right one a caveat: the
          columns below are headed by the questions they answer, and neither
          heading outranks the other. */}
      <Typography
        component="h2"
        sx={{
          fontFamily: 'Manrope, sans-serif',
          fontWeight: 800,
          fontSize: { xs: 20, md: 22 },
          lineHeight: 1.25,
          color: TEXT,
          // Was mb { xs: 2.5, md: 3 }, pb { xs: 2, md: 2.5 }.
          mb: 2,
          pb: 1.5,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        {PLAN_OPTIONS_HEADER}
      </Typography>

      {/* ── Two rows, so opening one column cannot move the other ──────────
          Row one holds each column's always-visible part; row two holds what
          "Show more" reveals. Row one stretches both cells to one height and
          the toggles sit at its foot, so in the collapsed state the two
          columns are the same height and their toggles share a line — even
          though pay-as-you-go's intro runs a line longer.

          Had each column been a single cell, opening the left one would have
          stretched the right one too and dragged its toggle down to the new
          bottom, away from the text it belongs to. With the reveal in its own
          row, the other column's cell there is simply empty.

          Areas rather than DOM order place the cells, so the DOM stays
          heading → intro → toggle → more for each column in turn: a screen
          reader, and the stacked mobile layout, both get one column at a
          time. The middle track is still the divider, spanning both rows. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1px 1fr' },
          gridTemplateAreas: {
            xs: '"top0" "more0" "top1" "more1"',
            md: '"top0 divider top1" "more0 divider more1"',
          },
          columnGap: { md: 4 },
        }}
      >
        {/* A grid cell rather than a border, so it spans the taller column
            without either having to know the other's height. Hidden when
            the columns stack, where a horizontal rule would read as a
            section break rather than a separator. */}
        <Box
          aria-hidden="true"
          sx={{ gridArea: 'divider', display: { xs: 'none', md: 'block' }, bgcolor: BORDER }}
        />

        {options.map((option, index) => {
          const open = Boolean(expanded[option.key]);
          const moreId = `${idBase}-${option.key}-more`;

          return (
            <React.Fragment key={option.key}>
              <Box
                sx={{
                  gridArea: `top${index}`,
                  display: 'flex',
                  flexDirection: 'column',
                  // Stacked, the second column needs its own space above it; side
                  // by side, the column gap does that job.
                  mt: { xs: index > 0 ? 3 : 0, md: 0 },
                }}
              >
                {/* REQUESTED (QA): the SUBSCRIPTION / PAY-AS-YOU-GO headers
                    "need to be more prominent". Was Inter 700 at 11px, spaced
                    out like a caption; now a bold label that reads before the
                    question under it. Same text, same colour for both, so
                    neither side outranks the other. */}
                <Typography sx={EYEBROW_SX}>
                  {option.eyebrow}
                </Typography>

                <Typography
                  // h3, under the card's h2: the columns are parts of what the
                  // header names, and a screen reader should hear them that way.
                  component="h3"
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 800,
                    fontSize: { xs: 18, md: 19 },
                    lineHeight: 1.3,
                    color: TEXT,
                    mb: 1,
                  }}
                >
                  {option.heading}
                </Typography>

                {option.intro.map((segments, i) => (
                  <Line key={i} segments={segments} onGoTo={onGoTo} sx={{ mb: 0.75 }} />
                ))}

                {/* mt:auto pins the toggle to the foot of row one, so both
                    toggles share a line whichever intro is longer. */}
                <Box sx={{ mt: 'auto', pt: 0.5 }}>
                  <MoreToggle
                    open={open}
                    onClick={() => toggle(option.key)}
                    controls={moreId}
                  />
                </Box>
              </Box>

              {/* start, not stretch: a stretched cell would hold an open
                  column's height open in the collapsed column beside it. */}
              <Box sx={{ gridArea: `more${index}`, alignSelf: 'start', minWidth: 0 }}>
                <Collapse in={open} timeout={reduceMotion ? 0 : COLLAPSE_MS} id={moreId}>
                  <Box sx={{ pt: 1.5 }}>
                    {/* REQUESTED (QA): "use line spacing and text exactly as
                        shown" — the terms as ONE paragraph, worded as
                        specified, with a blank line's space before the button.
                        OLD: a line per sentence (option.points) plus an italic
                        footnote, spaced at lineHeight 1.7 — see planOptions. */}
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 14,
                        lineHeight: 1.6,
                        color: TEXT_MUTED,
                        mb: 2,
                      }}
                    >
                      {option.body}
                    </Typography>

                    {/* REQUESTED (QA): a real button — orange with white text
                        for subscriptions, white with green text for
                        pay-as-you-go. Still a scroll, not a navigation.
                        OLD: <ScrollLink onClick={() => onGoTo?.(option.target)}>
                        OLD:   {option.linkLabel}
                        OLD: </ScrollLink> */}
                    <Button
                      type="button"
                      disableElevation
                      data-variant={option.linkVariant}
                      onClick={() => onGoTo?.(option.target)}
                      sx={{ ...CTA_BASE, ...(CTA_VARIANTS[option.linkVariant] || CTA_VARIANTS.secondary) }}
                    >
                      {option.linkLabel}
                    </Button>
                  </Box>
                </Collapse>
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export { PLAN_OPTION_TARGET };
export default PlanOptionsCard;
